/** Compare Accounts slice — the reference cmpModel() (design-reference script lines 1873–2067), ported verbatim.
 *  Period + account selection gate, stat cards, metric rows with best-cell highlight, radar, cumulative curves,
 *  gross/net bars and per-account symbol breakdown. Roster from c.data.compare.accounts; ledger from c.trades(). */
import type { Ctx } from './ctx';
import type { TradeTuple } from './helpers';
import type { ViewModel } from '@/screens/generated/view-model';

interface PerAcct {
  id: string;
  nickname: string;
  colour: string;
  ink: string;
  trades: TradeTuple[];
  totalTrades: number;
  totalPnl: number;
  winRate: number;
  wins: number;
  losses: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: number;
  grossProfit: number;
  grossLoss: number;
  largestWin: number;
  largestLoss: number;
  balance: number;
}
type MetricKey =
  | 'totalTrades'
  | 'totalPnl'
  | 'winRate'
  | 'wins'
  | 'avgWin'
  | 'avgLoss'
  | 'profitFactor'
  | 'grossProfit'
  | 'grossLoss'
  | 'largestWin'
  | 'largestLoss'
  | 'balance';
interface Metric {
  label: string;
  k: MetricKey;
  fmt: (v: number, a: PerAcct) => string;
  hl?: boolean;
  invert?: boolean;
}
interface Axis {
  m: string;
  get: (a: PerAcct) => number;
  cap: number;
  capLabel: string;
}

export function buildCompare(c: Ctx) {
  const S = c.S;
  const CMP_ACCOUNTS = c.data.compare.accounts;
  const money = (v: number) =>
    (v < 0 ? '−$' : '$') +
    Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const PERIOD: Record<string, number> = { week: 6, month: 30, quarter: 90, all: 9999 };
  const maxOff = PERIOD[S.cmpPeriod];
  const rows = c.trades().filter((r) => maxOff !== undefined && r[1] <= maxOff);

  const per: PerAcct[] = CMP_ACCOUNTS.filter((a) => S.cmpSel.indexOf(a.id) >= 0).map((a) => {
    const t = rows.filter((r) => r[2] === a.id);
    const wins = t.filter((r) => r[8] > 0),
      losses = t.filter((r) => r[8] < 0);
    const grossProfit = wins.reduce((s, r) => s + r[8], 0);
    const grossLoss = Math.abs(losses.reduce((s, r) => s + r[8], 0));
    return {
      id: a.id,
      nickname: a.nickname,
      colour: a.colour,
      ink: a.ink,
      trades: t,
      totalTrades: t.length,
      totalPnl: grossProfit - grossLoss,
      winRate: t.length ? (wins.length / t.length) * 100 : 0,
      wins: wins.length,
      losses: losses.length,
      avgWin: wins.length ? grossProfit / wins.length : 0,
      avgLoss: losses.length ? grossLoss / losses.length : 0,
      profitFactor: grossLoss ? grossProfit / grossLoss : 0,
      grossProfit,
      grossLoss,
      largestWin: wins.length ? Math.max(...wins.map((r) => r[8])) : 0,
      largestLoss: losses.length ? Math.min(...losses.map((r) => r[8])) : 0,
      balance: a.balance,
    };
  });

  const METRICS: Metric[] = [
    { label: 'Total Trades', k: 'totalTrades', fmt: (v) => String(v) },
    { label: 'Net P&L', k: 'totalPnl', fmt: money, hl: true },
    { label: 'Win Rate', k: 'winRate', fmt: (v) => v.toFixed(1) + '%' },
    { label: 'Wins / Losses', k: 'wins', fmt: (v, a) => a.wins + ' / ' + a.losses },
    { label: 'Avg Win', k: 'avgWin', fmt: money },
    {
      label: 'Avg Loss',
      k: 'avgLoss',
      fmt: (v) =>
        v === 0
          ? '$0.00'
          : '−$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      invert: true,
    },
    {
      label: 'Profit Factor',
      k: 'profitFactor',
      fmt: (v, a) => (a.grossLoss === 0 ? (a.grossProfit > 0 ? '∞' : '—') : v.toFixed(2)),
    },
    { label: 'Gross Profit', k: 'grossProfit', fmt: money },
    {
      label: 'Gross Loss',
      k: 'grossLoss',
      fmt: (v) =>
        v === 0
          ? '$0.00'
          : '−$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      invert: true,
    },
    { label: 'Largest Win', k: 'largestWin', fmt: money },
    { label: 'Largest Loss', k: 'largestLoss', fmt: money, invert: true },
    { label: 'Balance', k: 'balance', fmt: money },
  ];

  // radar: five axes normalised across the selection
  const volCap = Math.max(1, ...per.map((x) => x.totalTrades));
  const awCap = Math.max(1, ...per.map((x) => x.avgWin));
  const netCap = Math.max(1, ...per.map((x) => Math.max(0, x.totalPnl)));
  const AXES: Axis[] = [
    { m: 'Win Rate', get: (a) => a.winRate, cap: 100, capLabel: '100%' },
    {
      m: 'Profit Factor',
      get: (a) => (Math.min(a.profitFactor, 3) / 3) * 100,
      cap: 100,
      capLabel: 'PF 3.0+',
    },
    {
      m: 'Volume',
      get: (a) => a.totalTrades,
      cap: volCap,
      capLabel: volCap + (volCap === 1 ? ' trade' : ' trades'),
    },
    { m: 'Avg Win', get: (a) => a.avgWin, cap: awCap, capLabel: money(awCap) },
    { m: 'Net P&L', get: (a) => Math.max(0, a.totalPnl), cap: netCap, capLabel: money(netCap) },
  ];
  const R = 46,
    CX = 60,
    CY = 58;
  const radarPts = (a: PerAcct) =>
    AXES.map((ax, i) => {
      const frac = Math.max(0, Math.min(1, ax.get(a) / ax.cap));
      const ang = -Math.PI / 2 + (i / AXES.length) * Math.PI * 2;
      return (CX + Math.cos(ang) * R * frac).toFixed(1) + ',' + (CY + Math.sin(ang) * R * frac).toFixed(1);
    }).join(' ');

  // cumulative overlay
  const offs = Array.from(new Set(rows.map((r) => r[1]))).sort((a, b) => b - a);
  const series = per.map((a) => {
    let run = 0;
    const pts = offs.map((o) => {
      run += a.trades.filter((r) => r[1] === o).reduce((s, r) => s + r[8], 0);
      return run;
    });
    return { a, pts };
  });
  const allVals = series.reduce<number[]>((acc, s) => acc.concat(s.pts), [0]);
  const lo = Math.min(...allVals),
    hi = Math.max(...allVals);
  const span = Math.max(1, hi - lo);

  const pnlPeak = Math.max(
    1,
    ...per.map((a) => Math.max(Math.abs(a.grossProfit), Math.abs(a.grossLoss), Math.abs(a.totalPnl))),
  );

  return {
    cmpPeriods: (
      [
        ['week', 'This Week'],
        ['month', 'This Month'],
        ['quarter', 'This Quarter'],
        ['all', 'All Time'],
      ] as const
    ).map(([id, label]) => ({
      label,
      go: () => c.set({ cmpPeriod: id }),
      bg: S.cmpPeriod === id ? 'var(--rail)' : 'transparent',
      fg: S.cmpPeriod === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.cmpPeriod === id ? '600' : '500',
    })),
    cmpPicker: CMP_ACCOUNTS.map((a) => {
      const on = S.cmpSel.indexOf(a.id) >= 0;
      return {
        dot: a.colour,
        nickname: a.nickname,
        meta: a.broker + ' · ' + a.type,
        on,
        icon: on ? 'ph-check-square' : 'ph-square',
        iconFg: on ? a.colour : 'var(--ghost)',
        bd: on ? 'color-mix(in srgb, ' + a.colour + ' 45%, transparent)' : 'var(--l10)',
        bg: on ? 'color-mix(in srgb, ' + a.colour + ' 8%, transparent)' : 'transparent',
        go: () => c.set((s) => ({ cmpSel: on ? s.cmpSel.filter((x) => x !== a.id) : s.cmpSel.concat(a.id) })),
      };
    }),
    cmpEnough: per.length >= 2,
    cmpTooFew: per.length < 2,
    cmpHeads: per.map((a) => ({ nickname: a.nickname, ink: a.ink, dot: a.colour })),
    cmpStatCards: per.map((a) => {
      let run = 0;
      const series = offs
        .slice()
        .reverse()
        .map((o) => {
          run += a.trades.filter((r) => r[1] === o).reduce((s, r) => s + r[8], 0);
          return run;
        });
      const lo = Math.min(0, ...series),
        hi = Math.max(0, ...series),
        sp = Math.max(1, hi - lo);
      return {
        nickname: a.nickname,
        ink: a.ink,
        colour: a.colour,
        net: money(a.totalPnl),
        netFg: a.totalPnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        wr: a.winRate.toFixed(0) + '%',
        trades: a.totalTrades === 1 ? '1 trade' : a.totalTrades + ' trades',
        spark:
          series.length > 1
            ? 'M' +
              series
                .map(
                  (v, i) =>
                    ((i / (series.length - 1)) * 120).toFixed(1) +
                    ',' +
                    (26 - ((v - lo) / sp) * 24).toFixed(1),
                )
                .join(' L')
            : '',
      };
    }),
    cmpWinner:
      per.length >= 2
        ? (() => {
            const w = per.slice().sort((x, y) => y.totalPnl - x.totalPnl)[0];
            return w
              ? { nickname: w.nickname, ink: w.ink, net: money(w.totalPnl), wr: w.winRate.toFixed(0) + '%' }
              : undefined;
          })()
        : undefined,
    cmpCols: 'minmax(120px,1.2fr) ' + per.map(() => 'minmax(96px,1fr)').join(' '),
    cmpRows: METRICS.map((m, mi) => {
      const vals = per.map((a) => (m.invert ? -Number(a[m.k] || 0) : Number(a[m.k] || 0)));
      const best = vals.indexOf(Math.max(...vals));
      return {
        label: m.label,
        rowBg: mi % 2 === 1 ? 'var(--l3)' : 'transparent',
        cells: per.map((a, i) => ({
          v: m.fmt(a[m.k], a),
          best: i === best,
          fw: i === best ? '600' : '500',
          c: m.hl ? (a[m.k] >= 0 ? 'var(--pos)' : 'var(--neg)') : 'var(--text2)',
        })),
      };
    }),
    cmpRadar: per.map((a) => {
      const wins = AXES.filter((ax) => {
        const vals = per.map((x) => ax.get(x));
        const top = Math.max(...vals);
        return top > 0 && ax.get(a) === top && vals.filter((v) => v === top).length === 1;
      }).map((ax) => ax.m);
      return {
        nickname: a.nickname,
        colour: a.colour,
        ink: a.ink,
        pts: radarPts(a),
        leads: wins.length ? 'Leads ' + wins.join(', ') : 'Leads no axis',
      };
    }),
    cmpRadarAxes: AXES.map((ax, i) => {
      const ang = -Math.PI / 2 + (i / AXES.length) * Math.PI * 2;
      const cx = Math.cos(ang),
        sy = Math.sin(ang);
      const lx = CX + cx * (R + 8),
        ly = CY + sy * (R + 8);
      const align = cx > 0.25 ? 'left' : cx < -0.25 ? 'right' : 'center';
      const tx = align === 'left' ? '0' : align === 'right' ? '-100%' : '-50%';
      const ty = sy < -0.25 ? '-100%' : sy > 0.25 ? '0' : '-50%';
      return {
        x2: (CX + cx * R).toFixed(1),
        y2: (CY + sy * R).toFixed(1),
        lxPct: ((lx / 120) * 100).toFixed(2) + '%',
        lyPct: ((ly / 124) * 100).toFixed(2) + '%',
        tf: 'translate(' + tx + ',' + ty + ')',
        align,
        label: ax.m,
        capLabel: ax.capLabel,
      };
    }),
    cmpRadarRings: [0.33, 0.66, 1].map((f) => ({
      r: (R * f).toFixed(1),
      tyPct: (((CY - R * f) / 124) * 100).toFixed(2) + '%',
      pct: Math.round(f * 100) + '%',
    })),
    cmpRadarNote:
      'Each axis is normalised to the strongest of the ' +
      per.length +
      ' selected accounts — the outer ring is that leader, not an absolute maximum. Win Rate and Profit Factor use fixed scales.',
    cmpCurves: series.map((s) => {
      const end = s.pts.length ? (s.pts[s.pts.length - 1] ?? 0) : 0;
      return {
        nickname: s.a.nickname,
        colour: s.a.colour,
        ink: s.a.ink,
        end: money(end),
        endFg: end >= 0 ? 'var(--pos)' : 'var(--neg)',
        path:
          s.pts.length > 1
            ? 'M' +
              s.pts
                .map(
                  (v, i) =>
                    ((i / (s.pts.length - 1)) * 420).toFixed(1) +
                    ',' +
                    (150 - ((v - lo) / span) * 138).toFixed(1),
                )
                .join(' L')
            : '',
      };
    }),
    cmpCurveNote:
      'Running total per account across ' +
      (offs.length === 1 ? '1 trading day' : offs.length + ' trading days') +
      ' — every line shares one scale, ' +
      money(lo) +
      ' to ' +
      money(hi) +
      '.',
    cmpCurveFrom: offs.length ? (offs[0] === 0 ? 'Today' : offs[0] + 'd ago') : '—',
    cmpCurveTo: offs.length ? (offs[offs.length - 1] === 0 ? 'Today' : offs[offs.length - 1] + 'd ago') : '—',
    cmpZeroY: (150 - ((0 - lo) / span) * 138).toFixed(1),
    cmpBars: per.map((a) => ({
      nickname: a.nickname,
      colour: a.colour,
      ink: a.ink,
      gp: money(a.grossProfit),
      gl:
        a.grossLoss === 0
          ? '$0.00'
          : '−$' +
            a.grossLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      net: money(a.totalPnl),
      gpW: ((a.grossProfit / pnlPeak) * 100).toFixed(1) + '%',
      glW: ((a.grossLoss / pnlPeak) * 100).toFixed(1) + '%',
      netW: ((Math.abs(a.totalPnl) / pnlPeak) * 100).toFixed(1) + '%',
      netBg: a.totalPnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      netFg: a.totalPnl >= 0 ? 'var(--pos)' : 'var(--neg)',
    })),
    cmpSymbols: per.map((a) => {
      const m: Record<string, { n: number; pnl: number }> = {};
      a.trades.forEach((r) => {
        const e = (m[r[3]] = m[r[3]] || { n: 0, pnl: 0 });
        e.n++;
        e.pnl += r[8];
      });
      const sym = (k: string) => m[k] ?? { n: 0, pnl: 0 };
      const peak = Math.max(1, ...Object.keys(m).map((k) => Math.abs(sym(k).pnl)));
      return {
        nickname: a.nickname,
        colour: a.colour,
        ink: a.ink,
        empty: Object.keys(m).length === 0,
        syms: Object.keys(m)
          .sort((x, y) => sym(y).pnl - sym(x).pnl)
          .map((k) => ({
            sym: k,
            n: sym(k).n === 1 ? '1 trade' : sym(k).n + ' trades',
            pnl: money(sym(k).pnl),
            pnlFg: sym(k).pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
            w: ((Math.abs(sym(k).pnl) / peak) * 100).toFixed(1) + '%',
            bar: sym(k).pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          })),
      };
    }),
  } satisfies Partial<ViewModel>;
}
export type CompareModel = ReturnType<typeof buildCompare>;
